import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getStandings } from '@/services/dashboard/dashboard';

/**
 * Composable de clasificación (standings) del dashboard.
 * SIN soporte multi-season — trabaja a nivel de división.
 * NO extiende useDashboardTab por ser un endpoint diferente.
 *
 * @returns {{
 *     standingsData: import('vue').Ref<Array>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     standingsTable: import('vue').ComputedRef<Array>,
 *     selectedJornada: import('vue').Ref<number|null>,
 *     fetchStandings: (jornada?: number) => Promise<void>,
 * }}
 */
export function useStandings() {
    const standingsData = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const selectedJornada = ref(null);

    let abortController = null;

    onUnmounted(() => {
        abortController?.abort();
    });

    const puntosChartData = computed(() => {
        if (!standingsData.value?.length) return [];
        const rows = [['Club', 'Puntos']];
        for (const row of standingsData.value) {
            rows.push([row.club?.nombre || row.club_name || 'Club', Number(row.puntos)]);
        }
        return rows;
    });

    const standingsTable = computed(() => {
        return standingsData.value.map((entry, index) => ({
            posicion: entry.posicion || index + 1,
            clubName: entry.club?.nombre || '',
            shield: entry.club?.shield || null,
            puntos: entry.puntos || 0,
            pj: entry.pj || 0,
            pg: entry.pg || 0,
            pe: entry.pe || 0,
            pp: entry.pp || 0,
            gf: entry.gf || 0,
            gc: entry.gc || 0,
            dg: entry.dg || 0,
            racha: entry.racha || '',
        }));
    });

    async function fetchStandings(jornada) {
        abortController?.abort();
        abortController = new AbortController();

        loading.value = true;
        error.value = null;

        try {
            const params = {};
            if (jornada) params.jornada = jornada;
            const response = await getStandings(params, abortController.signal);
            standingsData.value = response.data?.data || [];
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            error.value = err.message || 'Error al cargar la clasificación';
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching standings:', err);
            }
        } finally {
            loading.value = false;
        }
    }

    onMounted(() => {
        fetchStandings();
    });

    return {
        standingsData,
        loading,
        error,
        puntosChartData,
        standingsTable,
        selectedJornada,
        fetchStandings,
    };
}
